import React, { useState, useEffect } from 'react';

// Self-service "Change Password" for an already-logged-in user — OTP-only,
// same philosophy as Login OTP (frontend/src/screens/Login.js): the OTP
// REPLACES the current-password step rather than sitting on top of it, so
// the user only needs to prove they still hold their registered mobile
// number, not remember their old password. Gated end-to-end by the same
// admin toggle surface (SMS Templates & Events > PASSWORD_RESET_OTP).
//
// Backend: POST /api/auth/change-password/request + /verify (backend/authRoutes.js).

function formatSeconds(s) {
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const card = { width: 'min(420px, 100%)', background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)', borderRadius: 12, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const input = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--theme-border, #d1d5db)', fontSize: 14, marginTop: 4 };
const label = { fontSize: 13, fontWeight: 600, color: 'var(--theme-text, #374151)' };
const btnPrimary = { padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--theme-primary, #0d8f83)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
const btnGhost = { padding: '9px 18px', borderRadius: 8, border: '1px solid var(--theme-border, #d1d5db)', background: 'transparent', color: 'var(--theme-text, #374151)', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
const errorBox = { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 };

function ChangePasswordModal({ token, onClose }) {
  // step: 'checking' | 'disabled' | 'request' | 'verify' | 'done'
  const [step, setStep] = useState('checking');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [challengeId, setChallengeId] = useState('');
  const [maskedMobile, setMaskedMobile] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);

  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/change-password/enabled', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json().catch(() => ({ enabled: false }));
        if (cancelled) return;
        setStep(data?.enabled ? 'request' : 'disabled');
      } catch { if (!cancelled) setStep('disabled'); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (step !== 'verify' || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [step, secondsLeft]);

  const requestOtp = async () => {
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/change-password/request', { method: 'POST', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not send OTP'); return; }
      setChallengeId(data.challenge_id);
      setMaskedMobile(data.masked_mobile || '');
      setSecondsLeft(Number(data.expires_in_seconds || 300));
      setOtp(''); setNewPassword(''); setConfirmPassword('');
      setStep('verify');
    } catch { setError('Network error'); } finally { setLoading(false); }
  };

  const verifyAndChange = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) { setError('Enter the 6-digit OTP'); return; }
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/change-password/verify', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ challenge_id: challengeId, otp, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not change password'); return; }
      setStep('done');
    } catch { setError('Network error'); } finally { setLoading(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} role="dialog" aria-label="Change Password" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Change Password</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--theme-text-muted, #6b7280)' }}>✕</button>
        </div>

        {error && <div style={errorBox} role="alert">{error}</div>}

        {step === 'checking' && <div style={{ color: 'var(--theme-text-muted)', fontSize: 14 }}>Checking availability…</div>}

        {step === 'disabled' && (
          <div>
            <p style={{ fontSize: 14, color: 'var(--theme-text-muted)' }}>
              Change Password via OTP isn't enabled for this system yet. Contact your administrator (Admin &gt; SMS Templates &amp; Events &gt; PASSWORD_RESET_OTP).
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnGhost} onClick={onClose}>Close</button>
            </div>
          </div>
        )}

        {step === 'request' && (
          <div>
            <p style={{ fontSize: 14, color: 'var(--theme-text-muted)' }}>
              We'll send a 6-digit OTP to your registered mobile number to verify it's you — no need to remember your current password.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btnGhost} onClick={onClose} disabled={loading}>Cancel</button>
              <button style={btnPrimary} onClick={requestOtp} disabled={loading}>{loading ? 'Sending…' : 'Send OTP'}</button>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <form onSubmit={verifyAndChange}>
            <p style={{ fontSize: 14, color: 'var(--theme-text-muted)', marginTop: 0 }}>
              A 6-digit OTP has been sent to <strong>+91 {maskedMobile}</strong>.
            </p>
            <label style={label}>OTP Code
              <input style={input} inputMode="numeric" maxLength={6} placeholder="6-digit OTP" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
            </label>
            <div style={{ fontSize: 12, color: secondsLeft > 0 ? 'var(--theme-text-muted)' : '#b91c1c', margin: '6px 0 14px' }}>
              {secondsLeft > 0 ? `OTP expires in ${formatSeconds(secondsLeft)}` : 'OTP expired — please resend.'}
            </div>
            <label style={label}>New Password
              <input style={input} type="password" placeholder="At least 8 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </label>
            <div style={{ marginTop: 12 }} />
            <label style={label}>Confirm New Password
              <input style={input} type="password" placeholder="Re-enter new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
              <button type="button" onClick={requestOtp} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--theme-primary, #0d8f83)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                Resend OTP
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={btnGhost} onClick={onClose} disabled={loading}>Cancel</button>
                <button type="submit" style={btnPrimary} disabled={loading}>{loading ? 'Changing…' : 'Change Password'}</button>
              </div>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div>
            <p style={{ fontSize: 14, color: '#166534' }}>✓ Password changed successfully. Use it next time you log in.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnPrimary} onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChangePasswordModal;
